import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { BrandProvider } from './context/BrandContext';
import Login from './pages/Login';
import Register from './pages/Register';
import StudentDashboard from './pages/StudentDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import DeveloperDashboard from './pages/DeveloperDashboard';
import InstitutionAdminDashboard from './pages/InstitutionAdminDashboard';
import StudentPreview from './pages/StudentPreview';
import FacultyPreview from './pages/FacultyPreview';
import ModuleViewer from './pages/ModuleViewer';
import ChatAnalytics from './pages/ChatAnalytics';
import ChatEfficiencyDashboard from './pages/ChatEfficiencyDashboard';
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import GradebookPage from './pages/GradebookPage';
import MyGradesPage from './pages/MyGradesPage';
import DownloadsRedirect from './pages/DownloadsRedirect';
import RubricsPage from './pages/RubricsPage';
import SubmissionsPage from './pages/SubmissionsPage';
import GraderPage from './pages/GraderPage';
import LtiConfigPage from './pages/LtiConfigPage';
import PathwaysPage from './pages/PathwaysPage';
import AuthenticityPage from './pages/AuthenticityPage';
import DigestPreviewPage from './pages/DigestPreviewPage';
import UsersPage from './pages/UsersPage';
import LtiLaunchLanding from './pages/LtiLaunchLanding';
import StudyHub from './pages/StudyHub';
import StudySession from './pages/StudySession';
import StudyDuel from './pages/StudyDuel';
import DuelInbox from './pages/DuelInbox';
import StudyInsights from './pages/StudyInsights';
import FacultyInsights from './pages/FacultyInsights';
import ConceptMap from './pages/ConceptMap';
import ProtectedRoute from './components/ProtectedRoute';
import SetupGate from './components/SetupGate';
import AITutorLauncher from './components/AITutorLauncher';
import { TutorScopeProvider } from './context/TutorScopeContext';
import './App.css';

function App() {
  return (
    <BrowserRouter>
      <BrandProvider>
        <SetupGate>
          <AuthProvider>
            <TutorScopeProvider>
              <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register/:inviteToken" element={<Register />} />

              {/* Student Routes */}
              <Route path="/student" element={
                <ProtectedRoute roles={['student']}>
                  <StudentDashboard />
                </ProtectedRoute>
              } />
              <Route path="/student/module/:moduleId" element={
                <ProtectedRoute roles={['student', 'teacher', 'developer', 'institution_admin']}>
                  <ModuleViewer />
                </ProtectedRoute>
              } />

              {/* Teacher Routes */}
              <Route path="/teacher" element={
                <ProtectedRoute roles={['teacher', 'developer']}>
                  <TeacherDashboard />
                </ProtectedRoute>
              } />
              <Route path="/teacher/module/:moduleId" element={
                <ProtectedRoute roles={['teacher', 'developer', 'institution_admin']}>
                  <ModuleViewer />
                </ProtectedRoute>
              } />

              {/* Institution Admin Routes */}
              <Route path="/institution-admin" element={
                <ProtectedRoute roles={['institution_admin', 'developer']}>
                  <InstitutionAdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/institution-admin/module/:moduleId" element={
                <ProtectedRoute roles={['institution_admin', 'developer']}>
                  <ModuleViewer />
                </ProtectedRoute>
              } />

              {/* Developer Routes */}
              <Route path="/developer" element={
                <ProtectedRoute roles={['developer']}>
                  <DeveloperDashboard />
                </ProtectedRoute>
              } />
              <Route path="/developer/module/:moduleId" element={
                <ProtectedRoute roles={['developer']}>
                  <ModuleViewer />
                </ProtectedRoute>
              } />
              <Route path="/developer/analytics" element={
                <ProtectedRoute roles={['developer']}>
                  <ChatAnalytics />
                </ProtectedRoute>
              } />

              {/* Role-aware preview modes */}
              <Route path="/preview-as-faculty" element={
                <ProtectedRoute roles={['developer']}>
                  <FacultyPreview />
                </ProtectedRoute>
              } />
              <Route path="/preview-as-student" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher']}>
                  <StudentPreview />
                </ProtectedRoute>
              } />
              <Route path="/preview-as-student/module/:moduleId" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher']}>
                  <ModuleViewer />
                </ProtectedRoute>
              } />

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/login" replace />} />

              {/* Phase 1.7 — Rubrics + Submissions + AI-Assisted Grading */}
              <Route path="/rubrics" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher', 'student']}>
                  <RubricsPage />
                </ProtectedRoute>
              } />
              <Route path="/rubrics/:rubricId/submissions" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher', 'student']}>
                  <SubmissionsPage />
                </ProtectedRoute>
              } />
              <Route path="/submissions/:subId" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher', 'student']}>
                  <GraderPage />
                </ProtectedRoute>
              } />

              {/* Phase 1.7c — Chat Efficiency Dashboard (Patent Claim 21) */}
              <Route path="/chat-efficiency" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher']}>
                  <ChatEfficiencyDashboard />
                </ProtectedRoute>
              } />

              {/* Phase 1.7g — Executive Dashboard (4-panel class metrics) */}
              <Route path="/executive" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher']}>
                  <ExecutiveDashboard />
                </ProtectedRoute>
              } />

              {/* Phase 1.7e — Course Gradebook (composite + curve) */}
              <Route path="/gradebook" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher']}>
                  <GradebookPage />
                </ProtectedRoute>
              } />

              {/* Phase 1.7f — Student-facing My Grades view */}
              <Route path="/my-grades" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher', 'student']}>
                  <MyGradesPage />
                </ProtectedRoute>
              } />

              {/* Study Deck (Track A+B+C) — active recall + duel mode */}
              <Route path="/study" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher', 'student']}>
                  <StudyHub />
                </ProtectedRoute>
              } />
              <Route path="/study/session" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher', 'student']}>
                  <StudySession />
                </ProtectedRoute>
              } />
              <Route path="/study/duel" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher', 'student']}>
                  <StudyDuel />
                </ProtectedRoute>
              } />
              <Route path="/study/duels" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher', 'student']}>
                  <DuelInbox />
                </ProtectedRoute>
              } />
              <Route path="/study/insights" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher', 'student']}>
                  <StudyInsights />
                </ProtectedRoute>
              } />
              <Route path="/faculty/insights" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher']}>
                  <FacultyInsights />
                </ProtectedRoute>
              } />
              <Route path="/study/concept-map" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher', 'student']}>
                  <ConceptMap />
                </ProtectedRoute>
              } />

              {/* Phase 2.0 — Canvas LTI 1.3 admin + launch landing */}
              <Route path="/lti-config" element={
                <ProtectedRoute roles={['developer', 'institution_admin']}>
                  <LtiConfigPage />
                </ProtectedRoute>
              } />
              <Route path="/lti/launch-landing" element={<LtiLaunchLanding />} />

              {/* Phase 3 — Socratic Pathway visualization (Patent A, Claim 21 foundation) */}
              <Route path="/pathways" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher']}>
                  <PathwaysPage />
                </ProtectedRoute>
              } />
              <Route path="/pathways/:pathwayId" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher']}>
                  <PathwaysPage />
                </ProtectedRoute>
              } />

              {/* Phase 1.7i/j — Authenticity Roll-up faculty dashboard */}
              <Route path="/authenticity" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher']}>
                  <AuthenticityPage />
                </ProtectedRoute>
              } />
              <Route path="/authenticity/:replyId" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher']}>
                  <AuthenticityPage />
                </ProtectedRoute>
              } />

              {/* Phase 1.7i.1 — Faculty digest preview + send */}
              <Route path="/digest" element={
                <ProtectedRoute roles={['developer', 'institution_admin', 'teacher']}>
                  <DigestPreviewPage />
                </ProtectedRoute>
              } />

              {/* User management — list, role change, delete */}
              <Route path="/users" element={
                <ProtectedRoute roles={['developer', 'institution_admin']}>
                  <UsersPage />
                </ProtectedRoute>
              } />

              {/* Static /downloads/ index — only triggers when SPA catches
                  the directory request (production). On preview, the static
                  file serves directly and this route is never reached. */}
              <Route path="/downloads" element={<DownloadsRedirect />} />
              <Route path="/downloads/" element={<DownloadsRedirect />} />
            </Routes>
            {/* Single app-level AI Tutor. Reads scope + open-state from
                TutorScopeContext. No page should mount another tutor UI. */}
            <AITutorLauncher />
            </TutorScopeProvider>
          </AuthProvider>
        </SetupGate>
      </BrandProvider>
    </BrowserRouter>
  );
}

export default App;
